import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MoreHorizontal } from "lucide-react";

import {
  Button,
  ErrorState,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Money,
  Notice,
  PageHeader,
  SkeletonRows,
  Status,
  Surface,
  Table,
  TableContainer,
  TBody,
  Td,
  Th,
  THead,
  Tr,
  cn,
  focusRing,
} from "../../../../components/ui";
import { businessApi } from "../../../../services/api";
import PayrollInputsReview from "../components/PayrollInputsReview";
import { useCanManagePayroll } from "./HRPayrollLayout";
import PeriodActionDialog from "./PeriodActionDialog";
import { HR_PAYROLL_BASE, periodFacts, periodName, periodPath, periodStatus } from "./hrPayroll";

const resultStatus = (row) => String(row.payroll_result_storage_status || row.status || "").toLowerCase();
const resultId = (row) => row.payroll_result_id || row.id;
const netOf = (row) => row.payroll_result_net_pay_cents ?? row.net_pay_cents;

const RESULT_STATE = {
  computed: "Calculated",
  approved: "Approved by Finance",
  released: "Released to the employee",
  settled: "Paid",
  voided: "Voided",
};

// A result can be voided only while HR prepares the period; after approval corrections go through a Finance return (FIN-1).
const canVoid = (row) => !["approved", "released", "settled", "voided"].includes(resultStatus(row));

/** Secondary period changes, by status (payroll.runtime.service PERIOD_TRANSITIONS). */
function periodChanges(status) {
  const changes = [];
  if (status === "computed") {
    changes.push({
      key: "reopen",
      label: "Reopen for corrections",
      title: "Reopen this payroll for corrections?",
      description: "The calculated results are cleared. Correct the facts, then calculate again.",
      confirm: "Reopen for corrections",
      run: (id) => businessApi.openPayrollPeriod(id),
      done: "Reopened for corrections. Calculate again when the facts are right.",
    });
  }
  if (["collecting_inputs", "computed"].includes(status)) {
    changes.push({
      key: "revert",
      label: "Move back to not started",
      title: "Move this payroll back to not started?",
      description: "Any calculated results are cleared. Nothing is lost from attendance, leave or pay records.",
      confirm: "Move back",
      run: (id) => businessApi.revertPayrollToDraft(id),
      done: "Moved back to not started.",
    });
  }
  if (["draft", "collecting_inputs", "computed"].includes(status)) {
    changes.push({
      key: "cancel",
      danger: true,
      label: "Cancel this payroll",
      title: "Cancel this payroll?",
      description: "It closes without paying anyone, and its dates can be used by a new payroll. This can't be undone.",
      confirm: "Cancel payroll",
      run: (id) => businessApi.cancelPayrollPeriod(id),
      done: "Payroll cancelled.",
    });
  }
  return changes;
}

/**
 * The payslips of a period: the stored record of what was calculated for each employee (a small contextual list,
 * DESIGN.md › Data collections). A table from tablet width up; condensed rows on phones.
 */
function Payslips({ results, canManage, preparing, onVoid }) {
  if (!results.length) return null;
  const voidable = canManage && preparing;
  const number = (row) => (
    <Link to={`${HR_PAYROLL_BASE}/payslips/${resultId(row)}`} className={cn("rounded-control font-medium text-accent hover:underline", focusRing)}>
      {row.payslip_number || `#${resultId(row)}`}
    </Link>
  );
  const state = (row) => <Status tone={resultStatus(row) === "settled" ? "success" : "neutral"}>{RESULT_STATE[resultStatus(row)] || "Calculated"}</Status>;
  const net = (row) => <Money value={netOf(row)} className={resultStatus(row) === "voided" ? "text-tertiary line-through" : ""} />;
  return (
    <section aria-labelledby="payslips-title" className="space-y-2">
      <h2 id="payslips-title" className="scroll-mt-20 text-section-title text-foreground">Payslips ({results.length})</h2>
      <Surface>
        <div className="max-md:hidden">
          <TableContainer>
            <Table caption="Payslips in this payroll">
              <THead>
                <Tr>
                  <Th>Employee</Th>
                  <Th>Payslip</Th>
                  <Th>State</Th>
                  <Th numeric>Net pay</Th>
                  {voidable ? <Th><span className="sr-only">Actions</span></Th> : null}
                </Tr>
              </THead>
              <TBody>
                {results.map((row) => (
                  <Tr key={resultId(row)}>
                    <Td className="font-medium text-foreground">{row.member_name || "Employee"}</Td>
                    <Td className="tabular-nums">{number(row)}</Td>
                    <Td>{state(row)}</Td>
                    <Td numeric>{net(row)}</Td>
                    {voidable ? (
                      <Td actions>
                        {canVoid(row) ? <Button size="sm" variant="quiet" onClick={() => onVoid(row)}>Void</Button> : null}
                      </Td>
                    ) : null}
                  </Tr>
                ))}
              </TBody>
            </Table>
          </TableContainer>
        </div>
        <ul className="divide-y divide-subtle text-body-sm md:hidden" aria-label="Payslips in this payroll">
          {results.map((row) => (
            <li key={resultId(row)} className="flex items-start justify-between gap-3 px-4 py-2.5">
              <div className="min-w-0">
                <p className="font-medium text-foreground">{row.member_name || "Employee"}</p>
                <p className="text-meta text-secondary"><span className="tabular-nums">{number(row)}</span> · {state(row)}</p>
                {voidable && canVoid(row) ? <Button size="sm" variant="quiet" className="-ml-2 mt-1" onClick={() => onVoid(row)}>Void</Button> : null}
              </div>
              <span className="shrink-0 text-right font-medium text-foreground">{net(row)}</span>
            </li>
          ))}
        </ul>
      </Surface>
    </section>
  );
}

/** HR › Payroll › one period: the current step, what blocks it, the money, each employee, and the payslips. */
export default function HRPayrollPeriod() {
  const { periodId: rawId } = useParams();
  const periodId = Number(rawId);
  const navigate = useNavigate();
  const canManage = useCanManagePayroll();

  const [period, setPeriod] = useState(null);
  const [results, setResults] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | missing | error
  const [notice, setNotice] = useState(null);
  const [change, setChange] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [reviewKey, setReviewKey] = useState(0);

  const loadPeriod = useCallback(async () => {
    try {
      const res = await businessApi.getPayrollPeriod(periodId);
      setPeriod(res?.period || null);
      setState(res?.period ? "ready" : "missing");
    } catch (error) {
      setState(error?.response?.status === 404 || error?.status === 404 ? "missing" : "error");
    }
  }, [periodId]);

  const loadResults = useCallback(async () => {
    try {
      const res = await businessApi.getPayrollResults(periodId);
      setResults(Array.isArray(res?.rows) ? res.rows : []);
    } catch {
      setResults([]);
    }
  }, [periodId]);

  useEffect(() => {
    setState("loading");
    setNotice(null);
    if (!Number.isInteger(periodId) || periodId <= 0) {
      setState("missing");
      return;
    }
    loadPeriod();
    loadResults();
  }, [periodId, loadPeriod, loadResults]);

  const refresh = useCallback(async () => {
    await Promise.all([loadPeriod(), loadResults()]);
  }, [loadPeriod, loadResults]);

  const back = (
    <Button as={Link} to={HR_PAYROLL_BASE} variant="quiet" icon={ArrowLeft}>All payroll</Button>
  );

  if (state === "loading") return <SkeletonRows rows={5} columns={3} label="Loading the payroll" />;
  if (state === "missing" || state === "error") {
    return (
      <div className="space-y-6">
        <PageHeader title="Payroll period" actions={back} />
        <div className="rounded-panel border border-default bg-surface">
          <ErrorState
            title={state === "missing" ? "This payroll period wasn't found" : "This payroll couldn't be loaded"}
            description={state === "missing" ? "It may belong to another business, or the link is wrong." : "Check your connection and try again."}
            onRetry={state === "error" ? () => { setState("loading"); refresh(); } : undefined}
          />
        </div>
      </div>
    );
  }

  const status = periodStatus(period);
  const changes = canManage ? periodChanges(status) : [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={periodName(period)}
        description={periodFacts(period)}
        actions={
          <>
            {back}
            {changes.length ? (
              <Menu>
                <MenuTrigger asChild>
                  <Button icon={MoreHorizontal}>More</Button>
                </MenuTrigger>
                <MenuContent>
                  {changes.map((item) => (
                    <MenuItem key={item.key} tone={item.danger ? "danger" : undefined} onSelect={() => setChange(item)}>
                      {item.label}
                    </MenuItem>
                  ))}
                </MenuContent>
              </Menu>
            ) : null}
          </>
        }
      />

      {notice ? <Notice tone={notice.tone || "success"} role="status">{notice.text}</Notice> : null}

      <PayrollInputsReview
        key={`${periodId}-${reviewKey}`}
        periodId={periodId}
        period={period}
        payslips={results}
        canManage={canManage}
        onChanged={refresh}
        onStart={() => businessApi.openPayrollPeriod(periodId).then(refresh)}
        onOpenPeriod={(id) => navigate(periodPath(id))}
        onOpenEmployeePay={(memberId) => navigate(`/business/hr/employees/${memberId}/pay`)}
        onNotice={setNotice}
      />

      <Payslips results={results} canManage={canManage} preparing={["collecting_inputs", "computed"].includes(status)} onVoid={setVoiding} />

      {change ? (
        <PeriodActionDialog
          title={change.title}
          description={change.description}
          confirmLabel={change.confirm}
          danger={Boolean(change.danger)}
          failureTitle="Not changed"
          onClose={() => setChange(null)}
          onConfirm={async () => {
            await change.run(periodId);
            const done = change.done;
            setChange(null);
            await refresh();
            setReviewKey((value) => value + 1);
            setNotice({ tone: "success", text: done });
          }}
        />
      ) : null}
      {voiding ? (
        <PeriodActionDialog
          title={`Void ${voiding.member_name ? `${voiding.member_name}'s` : "this"} payslip?`}
          description="The calculated result is voided and leaves this payroll. Recalculate to create it again."
          confirmLabel="Void payslip"
          danger
          failureTitle="Payslip not voided"
          onClose={() => setVoiding(null)}
          onConfirm={async () => {
            await businessApi.voidPayrollResult(resultId(voiding));
            setVoiding(null);
            await refresh();
            setReviewKey((value) => value + 1);
            setNotice({ tone: "success", text: "Payslip voided." });
          }}
        >
          <p>Net pay <Money value={netOf(voiding)} className="font-medium text-foreground" />.</p>
        </PeriodActionDialog>
      ) : null}
    </div>
  );
}
